// A match with `subMatches` (an MLP tie — see generators/tieFormats.js) is
// flattened into one row per discipline, matching how production's own
// schedule already materializes each discipline as its own numbered game
// row. Row numbers are assigned AFTER `applyRowOrder` below, not here, so a
// saved reorder is reflected in the `#` column too.
const flattenRoundRows = (round) => {
  if (!round.matches?.length) return [];
  const rows = round.matches.flatMap((match) =>
    match.subMatches?.length > 0
      ? match.subMatches.map((subMatch) => ({
          key: `${match.id}-${subMatch.gameType}`,
          // Read directly by scheduleApi.js's serializeFlatRounds when
          // saving — the composite `key` above embeds it too, but a
          // caller shouldn't have to re-parse a key to get it back out.
          gameType: subMatch.gameType,
          gameTypeLabel: subMatch.gameTypeLabel,
          groupLabel: match.groupLabel,
          slotA: match.slotA,
          slotB: match.slotB,
          playersA: subMatch.playersA,
          playersB: subMatch.playersB,
          // Every discipline of an MLP tie shares one parent backend row
          // (see scheduleApi.js's own doc comment on why) — starting
          // scoring from any of these sub-rows starts the whole tie.
          liveData: match.liveData,
        }))
      : [
          {
            key: match.id,
            // A real, already-saved discipline row (hydrated back from the
            // backend — see scheduleApi.js's hydrateFlatRounds) IS a plain
            // match with these fields set directly, no subMatches wrapper
            // (see tieFormats.js's applyTieFormatToRounds: a match with
            // `gameType` already set is never re-expanded). Carrying them
            // through here is what makes a saved discipline row render
            // identically to the pre-save preview (gameTypeLabel meta text,
            // playersA/B name sub-line).
            gameType: match.gameType || null,
            gameTypeLabel: match.gameTypeLabel || null,
            groupLabel: match.groupLabel,
            slotA: match.slotA,
            slotB: match.slotB,
            playersA: match.playersA,
            playersB: match.playersB,
            placeholderA: match.placeholderA,
            placeholderB: match.placeholderB,
            liveData: match.liveData,
          },
        ]
  );
  // Real MLP always plays Dreambreaker last — the decisive game, only
  // needed if the other 4 disciplines end in a 2-2 tie — never subject to
  // the "space out" reordering below (that exists purely to rest players
  // across the OTHER 4 disciplines; letting it move Dreambreaker earlier
  // just because doing so happened to avoid a back-to-back repeat reads as
  // random and isn't how the format is actually played). Pulled out before
  // reordering and re-appended after, so it keeps its position regardless
  // of which players it shares with its neighbors. `legs` > 1 puts more
  // than one tie (and so more than one Dreambreaker) in the same round —
  // each one still lands at the very end, in their original relative order.
  const dreambreakers = rows.filter((row) => row.gameType === 'dreambreaker');
  const others = rows.filter((row) => row.gameType !== 'dreambreaker');
  return [...spaceOutAdjacentPlayers(others), ...dreambreakers];
};

// Every individual person a row involves — an MLP discipline row's specific
// pairing (`playersA`/`playersB`, e.g. one doubles pair per side) when
// present, else the two whole participants/teams themselves (`slotA`/
// `slotB`) as the finest identity available for a plain match.
const rowPlayerIds = (row) => {
  if (row.playersA?.length || row.playersB?.length) {
    return new Set([...(row.playersA || []).map((p) => p.id), ...(row.playersB || []).map((p) => p.id)]);
  }
  return new Set([row.slotA?.id, row.slotB?.id].filter((id) => id != null));
};

const sharesPlayer = (a, b) => {
  for (const id of a) if (b.has(id)) return true;
  return false;
};

// Every real app user id a row involves — the finest identity available,
// specifically for "does this concern user X" checks (the Filters panel's
// Player picker and My Games toggle — see FiltersModal.jsx). Differs from
// `rowPlayerIds` above (which stays at whatever granularity a row already
// displays, e.g. a whole team's own id) by also reaching into a team
// participant's `roster` when the row hasn't been split into per-discipline
// subMatches yet — a plain team-vs-team row's `slotA`/`slotB` are the TEAMS,
// but a real user watching "My Games" needs to match by being one of that
// team's roster members, not by the team happening to be named after them.
export const involvedUserIds = (row) => {
  const ids = new Set();
  if (row.playersA?.length || row.playersB?.length) {
    (row.playersA || []).forEach((p) => ids.add(p.id));
    (row.playersB || []).forEach((p) => ids.add(p.id));
    return ids;
  }
  [row.slotA, row.slotB].forEach((entry) => {
    if (entry?.id != null) ids.add(entry.id);
    (entry?.roster || []).forEach((p) => ids.add(p.id));
  });
  return ids;
};

// Whether a resolved row survives the Filters panel's current selections —
// every active dimension must match (courts/rounds/groups/participants/
// statuses are each OR'd within themselves, AND'd across dimensions), same
// faceted-filter convention as any filter panel. `filters` is the hook's
// applied (not draft) filter state; `null`/no active dimensions means
// nothing is filtered out. `currentUserId` is only non-null in a real
// tournament where the logged-in user maps to an actual participant (see
// useScheduleBuilder.js) — My Games has no effect otherwise.
//
// `watchedUserId` is My Games' mechanism (`involvedUserIds`) applied to
// ANY real user id, not just the logged-in one — how "search for a
// specific real person, then filter to them" (see FiltersModal.jsx's
// backend-search picker, for someone looking for a person other than
// themselves — a parent for their daughter, a coach for one of their
// players) actually filters once a result is picked. Same matching logic
// as My Games on purpose — a picked person and "yourself" aren't
// different KINDS of thing to match against, just different id sources.
export const rowMatchesFilters = (row, filters, currentUserId) => {
  if (!filters) return true;
  if (filters.courts?.length && !filters.courts.includes(row.court)) return false;
  if (filters.groups?.length && !filters.groups.includes(row.groupLabel)) return false;
  if (filters.statuses?.length && !filters.statuses.includes(row.status)) return false;
  if (filters.participants?.length) {
    const ids = [row.slotA?.id, row.slotB?.id];
    if (!filters.participants.some((id) => ids.includes(id))) return false;
  }
  if (filters.myGamesOnly && currentUserId != null && !involvedUserIds(row).has(currentUserId)) return false;
  if (filters.watchedUserId != null && !involvedUserIds(row).has(filters.watchedUserId)) return false;
  return true;
};

// Greedily re-sequences a round's rows so that, as much as possible, no two
// adjacent rows involve the same player. This matters almost entirely for an
// MLP tie's 5-game slate — all 5 disciplines draw from the same 8 players,
// so back-to-back rows in role-priority order (see tieFormats.buildMLPGames)
// can easily repeat someone with no rest between their own games. Two
// different ties in the same round never share a player, so this is a no-op
// there, and for any plain round (round-robin, single elimination, ...) it's
// also a no-op — each participant already appears in only one row per round
// by construction. Best-effort: if every remaining row conflicts with the
// last-placed one, it falls back to the next row in original order rather
// than leaving a gap.
const spaceOutAdjacentPlayers = (rows) => {
  if (rows.length <= 2) return rows;
  const remaining = rows.map((row) => ({ row, players: rowPlayerIds(row) }));
  const result = [];
  let lastPlayers = new Set();
  while (remaining.length) {
    let index = remaining.findIndex((entry) => !sharesPlayer(entry.players, lastPlayers));
    if (index === -1) index = 0;
    const [picked] = remaining.splice(index, 1);
    result.push(picked.row);
    lastPlayers = picked.players;
  }
  return result;
};

// Applies a saved "move up/down" order (an array of row keys) to a round's
// naturally-generated rows. Row keys no longer present (a regenerated
// schedule invalidates old keys) are dropped silently; rows not mentioned in
// the saved order (new since it was saved) are appended after it in their
// natural order, rather than lost.
const applyRowOrder = (rows, savedOrder) => {
  if (!savedOrder?.length) return rows;
  const byKey = new Map(rows.map((row) => [row.key, row]));
  const ordered = savedOrder.map((key) => byKey.get(key)).filter(Boolean);
  const orderedKeys = new Set(ordered.map((row) => row.key));
  return [...ordered, ...rows.filter((row) => !orderedKeys.has(row.key))];
};

// Resolves a row's effective court/settings/pairing from, in priority
// order: its own row-level override, its round's override, the backend-
// persisted override already saved on this match (row.liveData.
// overridden_match_settings — see scheduleApi.js's buildLiveData), then the
// auto-assigned default — field by field, not as one all-or-nothing object,
// so setting a match-level override for (say) custom rules alone doesn't
// accidentally blank out a court the organizer picked at the round level.
// `autoCourt` is this row's position-in-round pick from the plain
// round-robin cycle across configured courts (see MatchTable.jsx doc on
// how court auto-assignment works) — only used when nothing overrides it.
//
// The backend fallback matters because rowOverrides/roundOverrides are pure
// session state (see useScheduleBuilder.js's applyLoadedSchedule) — they're
// always empty right after a fresh load/refresh even though the actual
// column driving game creation (overridden_match_settings) was already
// correctly set, whether from this match's own gear icon or from a round-
// level override applying it here (applyRoundOverride fans the round's
// settings out to every row's own overridden_match_settings the same way).
// Without this fallback, the Match/Round Settings modals reopened looking
// un-customized after a refresh, even though scoring would still correctly
// use the saved settings — misleading, since there was no way to tell from
// the UI that anything had actually stuck.
const persistedOverride = (row) => {
  const settings = row.liveData?.overridden_match_settings;
  return settings ? { useCustomSettings: true, settings } : null;
};

const resolveOverrides = (row, rowOv, roundOv, autoCourt) => {
  const backendOv = persistedOverride(row);
  return {
    ...row,
    court: rowOv?.court ?? roundOv?.court ?? autoCourt,
    useCustomSettings: rowOv?.useCustomSettings ?? roundOv?.useCustomSettings ?? backendOv?.useCustomSettings ?? false,
    settings: (rowOv?.useCustomSettings ? rowOv.settings : undefined)
      ?? (roundOv?.useCustomSettings ? roundOv.settings : undefined)
      ?? backendOv?.settings
      ?? null,
    // Team Pairing (which specific roster players play this match) is
    // match-only — there's no single sensible player choice across every
    // match in a round, so `roundOv` never carries this.
    playersA: rowOv?.playersA || row.playersA,
    playersB: rowOv?.playersB || row.playersB,
    hasRowOverride: !!rowOv || !!backendOv,
    // A saved match's real checkin_status (scheduled/checking_in/ready/live/
    // completed — see MatchRow.jsx's STATUS_BADGES) once this schedule has
    // actually been saved to the backend (`row.liveData` — see scheduleApi.js);
    // 'draft' for a freshly-generated, not-yet-saved schedule, same as before
    // this field existed.
    status: row.liveData?.checkin_status || 'draft',
  };
};

// Flattens every round in one pass so row numbers stay sequential across the
// whole schedule regardless of which rounds are currently collapsed.
// `orderOverrides` is this list's saved row order, keyed by round `id` — see
// MatchTable's `moveRow` for how a reorder click produces it. `courts` is the
// organizer-configured court/field list (see useScheduleBuilder's
// `config.courts`); `roundOverrides`/`rowOverrides` are this list's gear-icon
// settings, keyed by round `id` / row key respectively. Both keyed by `id`
// (a GUID — see generators/idGen.js), not the round `round` NUMBER or the
// row's generator-assigned `key` alone being trusted to already be globally
// unique — see idGen.js for why a positional id isn't safe once several
// generator calls get merged into one shared list (group_knockout's "Full
// Schedule" view being the case that actually broke on this before).
//
// `filters`/`currentUserId` (see FiltersModal.jsx and useScheduleBuilder.js's
// `applyFilters`) narrow the result AFTER overrides are resolved — a Court
// filter, for instance, has to see the row's EFFECTIVE court (auto-assigned
// or overridden), not just its raw generator output. The Round filter drops
// a whole round (by its `label`) before even touching its rows; every other
// dimension drops individual rows. A round that had real rows before
// filtering but none after is dropped entirely — a round that never had any
// rows to begin with (an as-yet-undetermined bracket round) is left alone
// so RoundSection's own "Determined by previous round results" message
// still applies, unaffected by whatever filters happen to be active.
export const buildRoundsWithRows = (rounds, orderOverrides = {}, courts = [], roundOverrides = {}, rowOverrides = {}, filters = null, currentUserId = null) => {
  let counter = 0;
  const roundsFilterActive = !!filters?.rounds?.length;
  return (rounds || [])
    .filter((round) => !roundsFilterActive || filters.rounds.includes(round.label))
    .map((round) => {
      const roundOv = roundOverrides[round.id];
      const orderedRows = applyRowOrder(flattenRoundRows(round), orderOverrides[round.id]);
      const rows = orderedRows
        .map((row, index) => {
          const autoCourt = courts.length ? courts[index % courts.length].label : null;
          return resolveOverrides(row, rowOverrides[row.key], roundOv, autoCourt);
        })
        .filter((row) => rowMatchesFilters(row, filters, currentUserId))
        .map((row) => ({ ...row, number: ++counter }));
      return { round, rows };
    })
    .filter(({ round, rows }) => rows.length > 0 || !round.matches?.length);
};

// How many of the Filters panel's dimensions currently have a selection —
// drives the "Filters (3)" badge on GeneratedScheduleStep's button and
// whether an empty result set gets a "no matches for these filters" message
// (see MatchTable.jsx) instead of being read as a genuinely match-less
// schedule.
export const countActiveFilters = (filters) => {
  if (!filters) return 0;
  return (
    (filters.courts?.length ? 1 : 0) +
    (filters.rounds?.length ? 1 : 0) +
    (filters.groups?.length ? 1 : 0) +
    (filters.participants?.length ? 1 : 0) +
    (filters.statuses?.length ? 1 : 0) +
    (filters.myGamesOnly ? 1 : 0) +
    (filters.watchedUserId != null ? 1 : 0)
  );
};
